import type { CanvasEntityDefinition } from '@factorialco/f0-react'

import { dashboardCanvasEntity } from 'modules/ai/copilotActions/canvas/dashboards/dashboardEntity'
import { useDisplayDashboardAction } from 'modules/ai/copilotActions/canvas/dashboards/useDisplayDashboardAction'
import { dataDownloadCanvasEntity } from 'modules/ai/copilotActions/canvas/dataDownload/dataDownloadEntity'
import { useDataDownloadAction } from 'modules/ai/copilotActions/canvas/dataDownload/useDataDownloadAction'
import { formCanvasEntity } from 'modules/ai/copilotActions/canvas/forms/formEntity'
import { useFormCoAgentSync } from 'modules/ai/copilotActions/canvas/forms/useFormCoAgentSync'
import { useFormFillAction } from 'modules/ai/copilotActions/canvas/forms/useFormFillAction'
import { useFormSubmitAction } from 'modules/ai/copilotActions/canvas/forms/useFormSubmitAction'
import { useApproveMutationAction } from 'modules/ai/copilotActions/global/useApproveMutationAction'
import { useClarifyingQuestionAction } from 'modules/ai/copilotActions/global/clarifyingQuestion/useClarifyingQuestionAction'
import { useCreateEntityAction } from 'modules/ai/copilotActions/global/useCreateEntityAction'
import { useMessageSourcesAction } from 'modules/ai/copilotActions/global/useMessageSourcesAction'
import { useNavigateAction } from 'modules/ai/copilotActions/global/useNavigateAction'
import { useNotifyMutationExecutedAction } from 'modules/ai/copilotActions/global/useNotifyMutationExecutedAction'
import { useOpenActivityAction } from 'modules/ai/copilotActions/global/useOpenActivityAction'
import { useOrchestratorThinkingAction } from 'modules/ai/copilotActions/global/useOrchestratorThinkingAction'
import { useSetLocalStorageAction } from 'modules/ai/copilotActions/global/useSetLocalStorageAction'
import { useBookAMeetingCardAction } from 'modules/ai/copilotActions/upsellingKit/useBookAMeetingCardAction'
import { useDemoCardAction } from 'modules/ai/copilotActions/upsellingKit/useDemoCardAction'
import { useFAQCardAction } from 'modules/ai/copilotActions/upsellingKit/useFAQCardAction'
import { useMessageCreditsWarningAction } from 'modules/ai/copilotActions/upsellingKit/useMessageCreditsWarningAction'
import { useModuleCardAction } from 'modules/ai/copilotActions/upsellingKit/useModuleCardAction'
import { useQuestionCardAction } from 'modules/ai/copilotActions/upsellingKit/useQuestionCardAction'

/**
 * Canvas renderers keyed by `CanvasContent["type"]`. Passed to
 * `F0AiChatProvider.canvasEntities` so the canvas panel can paint the right
 * UI when the host app calls `openCanvas({ type, … })`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const canvasEntities: Record<string, CanvasEntityDefinition<any>> = {
  form: formCanvasEntity,
  dashboard: dashboardCanvasEntity,
  dataDownload: dataDownloadCanvasEntity,
}

/**
 * Registers every CopilotKit action factorial exposes. Call once from a
 * component already inside `<CopilotKit>` so each `useCopilotAction` hook
 * mounts in the right context.
 *
 * Adding a new product: drop a folder under `copilotActions/` (or under
 * `copilotActions/canvas/` if it drives a canvas surface) and add a line in
 * the matching section below importing directly from the action's file.
 */
export const useRegisterCopilotActions = () => {
  // global (chat shell + app behaviour)
  useOrchestratorThinkingAction()
  useMessageSourcesAction()
  useClarifyingQuestionAction()
  useNotifyMutationExecutedAction()
  useOpenActivityAction()
  useNavigateAction()
  useCreateEntityAction()
  useApproveMutationAction()
  useSetLocalStorageAction()

  // upselling kit
  useDemoCardAction()
  useFAQCardAction()
  useModuleCardAction()
  useBookAMeetingCardAction()
  useQuestionCardAction()
  useMessageCreditsWarningAction()

  // canvas
  useFormCoAgentSync()
  useFormFillAction()
  useFormSubmitAction()
  useDisplayDashboardAction()
  useDataDownloadAction()
}
